import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, LinearProgress, Card, CardContent } from '@mui/material';
import { resolveShowCost } from '../../../system/report-visibility';
import { formatUsd } from '../../../system/report-formatters';

function CreativeDetailTabs({ rowData, appVars, sDate, eDate, reportProfile, showRevenue = true }) {
  const [detailData, setDetailData] = useState([[], [], [], []]);
  const [loading, setLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const showCost = resolveShowCost(reportProfile);
  //       console.log('AppVars',appVars);



  useEffect(() => {
    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      "campaign": rowData.id,
      "cid": sessionStorage.getItem('rid'),
      "sdate": sessionStorage.getItem('sDate'),
      "edate": sessionStorage.getItem('eDate')
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };

    fetch(`${appVars.api_url}/stackadapt/creatives/`, requestOptions)
      .then(res => res.json())
      .then(creatives => {
       // console.log(creatives);
        setDetailData([creatives, [], [], []]);
      })
      .catch(err => console.error("Failed to fetch creatives:", err))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [rowData.id, rowData.cid, sDate, eDate, appVars.api_url]);

  const normalizeImageItems = (source) => {
    const output = [];
    const push = (val, raw) => {
      if (val == null) return;
      if (Array.isArray(val)) {
        val.forEach((item) => push(item, raw));
        return;
      }
      if (typeof val === 'object') {
        const url =
          val.IMAGE_ASSET_URL ||
          val.IMAGE_URL ||
          val.URL ||
          val.url ||
          val.image_url ||
          val.image ||
          val.name;
        const name = val.NAME || val.name || val.filename || val.FILE_NAME || val.TITLE || val.title;
        if (url) {
          output.push({ url, name: name || url, raw: val });
          return;
        }
        const key = ['IMAGE_ASSET_URL', 'IMAGE_URL', 'URL', 'url', 'image_url', 'image', 'name'].find((k) => val[k] != null);
        if (key) {
          push(val[key], val);
        }
        return;
      }
      const text = String(val).trim();
      if (text) output.push({ url: text, name: text, raw });
    };

    if (source && typeof source === 'object' && !Array.isArray(source)) {
      const key = ['IMAGE_ASSET_URL', 'IMAGE_URL', 'URL', 'url', 'image_url', 'image', 'name'].find((k) => source[k] != null);
      if (key) {
        push(source[key], source);
        return output;
      }
    }

    push(source, source);
    return output;
  };

  const imageItems = useMemo(() => normalizeImageItems(detailData[0]), [detailData]);

  const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
  const formatMoney = (value) => formatUsd(Number(value || 0));
  const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

  const pickMetric = (row, keys) => {
    if (!row || typeof row !== 'object') return null;
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null) return Number(row[key]);
    }
    const lowerKeys = keys.map((key) => String(key).toLowerCase());
    const rowKey = Object.keys(row).find((k) => lowerKeys.includes(String(k).toLowerCase()));
    if (rowKey) return Number(row[rowKey]);
    return null;
  };

  const getStats = (row) => {
    if (!row || typeof row !== 'object') return null;
    const impressions = pickMetric(row, ['IMPRESSIONS', 'impressions']);
    const clicks = pickMetric(row, ['CLICKS', 'clicks']);
    const conversions = pickMetric(row, ['CONVERSIONS', 'conversions']);
    const cost = pickMetric(row, ['COST', 'cost', 'SPEND', 'spend']);
    const revenue = pickMetric(row, ['REVENUE', 'revenue']);
    const avgGiftRaw = pickMetric(row, ['AVG_GIFT', 'avg_gift', 'avgGift', 'AVG_GIFT_VALUE']);
    const ctrRaw = pickMetric(row, ['CTR', 'ctr']);
    const convRateRaw = pickMetric(row, ['CONV_RATE', 'CONVRATE', 'convrate', 'convRate']);
    const ctr = ctrRaw ?? (impressions && impressions > 0 ? (Number(clicks || 0) / impressions) * 100 : null);
    const convRate = convRateRaw ?? (clicks && clicks > 0 ? (Number(conversions || 0) / clicks) * 100 : null);
    const roas = cost && cost > 0 && revenue != null ? revenue / cost : null;
    const avgGift = avgGiftRaw ?? (conversions && conversions > 0 && revenue != null ? revenue / conversions : null);
    const hasAny = [impressions, clicks, conversions, cost, revenue, avgGift, ctr, convRate].some((val) => val !== null && val !== undefined);
    return { impressions, clicks, conversions, cost, revenue, avgGift, ctr, convRate, roas, hasAny };
  };

  const renderStats = (row) => {
    const stats = getStats(row);
    if (!stats?.hasAny) return null;
    const items = [
      { label: 'Impr', value: stats.impressions, format: formatNumber },
      { label: 'Clicks', value: stats.clicks, format: formatNumber },
      { label: 'Conv', value: stats.conversions, format: formatNumber },
      { label: 'CTR', value: stats.ctr, format: formatPercent },
      { label: 'Conv%', value: stats.convRate, format: formatPercent },
    ];
    if (showRevenue) {
      items.push(
        { label: 'Revenue', value: stats.revenue, format: formatMoney },
        { label: 'Avg Txn', value: stats.avgGift, format: formatMoney }
      );
    }
    if (showCost) {
      items.push(
        { label: 'Spend', value: stats.cost, format: formatMoney },
        { label: 'ROAS', value: stats.roas, format: (val) => (val != null ? `${Number(val).toFixed(2)}x` : '0.00x') }
      );
    }
    const visible = items.filter((item) => item.value !== null && item.value !== undefined);
    if (!visible.length) return null;
    return (
      <Box
        sx={{
          display: 'grid',
          gap: 0.5,
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          pt: 0.75,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        {visible.map((item) => (
          <Box key={item.label} sx={{ display: 'grid', gap: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              {item.label}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {item.format(item.value)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (


    <Box sx={{ pt: 0 }}>
      {loading ? <LinearProgress /> : (
        <>
          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
            {imageItems.length ? imageItems.map((item, index) => (
              <Card key={`${item.url}-${index}`} variant="outlined">
                <CardContent sx={{ display: 'grid', gap: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 120,
                      borderRadius: 1,
                      border: '1px solid rgba(0,0,0,0.08)',
                      background: '#fff',
                      p: 0.5
                    }}
                  >
                    <img
                      style={{ maxWidth: '100%', maxHeight: 140, cursor: 'pointer' }}
                      alt={item.name}
                      src={item.url}
                      onClick={() => {
                        setLightboxImage(item.url);
                        setLightboxOpen(true);
                      }}
                      onError={e => {
                        e.target.src = 'https://api.digitalwerks.com/imageFiles/no-image-icon.png';
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" noWrap title={item.name}>
                    {item.name}
                  </Typography>
                  {renderStats(item.raw)}
                </CardContent>
              </Card>
            )) : (
              <Typography variant="body2" color="text.secondary">No creatives available.</Typography>
            )}
          </Box>
        </>
      )}

      {lightboxOpen && (
        <Box
          onClick={() => setLightboxOpen(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'zoom-out'
          }}
        >
          <img
            src={lightboxImage}
            alt="Preview"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              boxShadow: '0 0 20px #000',
              borderRadius: 8,
            }}
          />
        </Box>
      )}
    </Box>

  );
}

export default CreativeDetailTabs;
